if (!customElements.get('notify-me-form')) {
  class NotifyMeForm extends HTMLElement {
    connectedCallback() {
      this.hasSubmitted = false;
      this.iframe = this.querySelector('iframe');

      if (this.iframe) {
        this.iframeLoadHandler = this.handleIframeLoad.bind(this);
        this.iframe.addEventListener('load', this.iframeLoadHandler);
      }

      this.bindForm();
    }

    disconnectedCallback() {
      this.unbindForm();

      if (this.iframe && this.iframeLoadHandler) {
        this.iframe.removeEventListener('load', this.iframeLoadHandler);
      }
    }

    /**
     * Finds the current content wrapper/form and wires up the submit
     * handler. Called on connect, and again after every content swap.
     */
    bindForm() {
      this.unbindForm();

      this.content = this.querySelector('.notify-me-form__content');
      this.form = this.querySelector('form');
      if (!this.form) return;

      // Submit natively into the hidden iframe (not via fetch) so
      // Shopify's own spam/CAPTCHA verification - which is bound to real
      // submit events - still runs and the request isn't rejected.
      if (this.iframe) {
        this.form.target = this.dataset.iframeName;
      }

      this.emailInput = this.form.querySelector('input[type="email"]');
      this.submitBtn = this.form.querySelector('[type="submit"]');
      this.formSubmitHandler = this.handleSubmit.bind(this);
      this.form.addEventListener('submit', this.formSubmitHandler);
    }

    /**
     * Removes the submit listener from the current form, if any.
     */
    unbindForm() {
      if (this.form && this.formSubmitHandler) {
        this.form.removeEventListener('submit', this.formSubmitHandler);
      }
    }

    /**
     * Checks whether a string looks like a valid email address.
     * @param {string} value - Value to check.
     * @returns {boolean}
     */
    isValidEmail(value) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
    }

    /**
     * Handles 'submit' events on the notify-me form.
     * @param {object} evt - Event object.
     */
    handleSubmit(evt) {
      if (this.emailInput && !this.isValidEmail(this.emailInput.value)) {
        evt.preventDefault();
        this.emailInput.setAttribute('aria-invalid', 'true');
        this.showError(window.theme?.strings?.notifyMeInvalidEmail);
        return;
      }

      // Let this submit through natively (into the hidden iframe).
      this.hasSubmitted = true;
      this.setLoading(true);
    }

    /**
     * Handles the hidden iframe finishing a navigation - either its
     * initial blank frame (ignored) or the result of a form submission.
     */
    handleIframeLoad() {
      if (!this.hasSubmitted) return;
      this.hasSubmitted = false;
      this.setLoading(false);

      let doc;
      try {
        doc = this.iframe.contentDocument;
      } catch (error) {
        this.showError();
        return;
      }

      const updated = doc && doc.querySelector(
        `notify-me-form[data-product-id="${CSS.escape(this.dataset.productId)}"][data-variant-id="${CSS.escape(this.dataset.variantId)}"] .notify-me-form__content`
      );

      if (updated && this.content) {
        this.content.innerHTML = updated.innerHTML;
        this.bindForm();
      } else {
        this.showError();
      }
    }

    /**
     * Toggles the submit button's loading state.
     * @param {boolean} isLoading - Whether the form is currently submitting.
     */
    setLoading(isLoading) {
      if (!this.submitBtn) return;
      this.submitBtn.disabled = isLoading;
      this.submitBtn.classList.toggle('is-loading', isLoading);
    }

    /**
     * Shows an inline error message without a full re-render.
     * @param {string} [message] - Error text to show (falls back to a generic message).
     */
    showError(message) {
      let errorEl = this.content && this.content.querySelector('.js-errors, .alert.errors');

      if (!errorEl && this.form) {
        errorEl = document.createElement('div');
        errorEl.className = 'alert errors notify-me-form__error';
        errorEl.setAttribute('role', 'alert');
        this.form.prepend(errorEl);
      }

      if (!errorEl) return;

      errorEl.textContent = message || window.theme?.strings?.notifyMeError || 'Something went wrong. Please try again.';
      errorEl.hidden = false;
    }
  }

  customElements.define('notify-me-form', NotifyMeForm);
}
